import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ErrorBoundary.module.css';
import { reportError } from './mapLayers/lib/report.js';

/*
  ErrorBoundary. Catches render/lifecycle errors under the router.
  Must be a class: getDerivedStateFromError / componentDidCatch are not available as hooks.

  Does NOT catch errors in event handlers, promises, or setTimeout (see BBB-516).
*/

// Copy chosen by Davin (Option B from the return handoff) and confirmed by him
// on 11/09/2026.
const FALLBACK_COPY = {
  title: "Something on this screen didn't load properly",
  body: "Nothing's been lost. Tap Try again, or head Home.",
};

// Labels arrive as props with these defaults rather than being hardcoded in the
// markup, per the repo convention CodeRabbit enforces. The brand default is the
// company name and is deliberately not "Headland" (see the note on line render).
const FALLBACK_LABELS = {
  brand: 'Black Bull Biochar',
  retry: 'Try again',
  goHome: 'Go to Home',
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Routed through the sanctioned reporter (BBB-526) rather than a bare
    // console call. reportError is a no-op outside a development build, which is
    // the right behaviour here: in production the farmer sees the fallback, and
    // nothing is shipped anywhere for a production console line to reach.
    reportError('[ErrorBoundary]', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  // Clear the error before navigating home. Without this, a crash on /home
  // leaves resetKey unchanged when Go to Home navigates to /home, so
  // componentDidUpdate never fires and the fallback stays on screen. This makes
  // the button work from any route, including the one it points at.
  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onGoHome) this.props.onGoHome();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          title={FALLBACK_COPY.title}
          body={FALLBACK_COPY.body}
          brand={this.props.brand}
          retryLabel={this.props.retryLabel}
          goHomeLabel={this.props.goHomeLabel}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          showDevDetail={process.env.NODE_ENV === 'development' ? this.state.error?.message : null}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({
  title,
  body,
  brand = FALLBACK_LABELS.brand,
  retryLabel = FALLBACK_LABELS.retry,
  goHomeLabel = FALLBACK_LABELS.goHome,
  onRetry,
  onGoHome,
  showDevDetail,
}) {
  return (
    <div className={styles.fallback} role="alert">
      {/*
        brand defaults to "Black Bull Biochar". It said "Headland" until BBB-516
        landed on uat. Headland is the retired app; this is bbb-app, which calls
        itself "Black Bull Biochar" in manifest.json and "BBB Dashboard" in the
        page title. It was the only "Headland" left anywhere in src, carried over
        from where the boundary was originally written. A farmer meeting a crash
        screen branded with a dead product is worse than the crash, so this is the
        company name rather than a new choice of wording.
      */}
      <div className={styles.brand}>{brand}</div>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.body}>{body}</p>
      {showDevDetail ? (
        <p className={styles.devDetail}>{showDevDetail}</p>
      ) : null}
      <div className={styles.actions}>
        <button type="button" className={styles.primaryButton} onClick={onRetry}>
          {retryLabel}
        </button>
        <button type="button" className={styles.secondaryButton} onClick={onGoHome}>
          {goHomeLabel}
        </button>
      </div>
    </div>
  );
}

/** Wires location + navigate so the class boundary can reset on route change. */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      resetKey={location.pathname}
      onGoHome={() => navigate('/home')}
    >
      {children}
    </ErrorBoundary>
  );
}

export { ErrorBoundary, ErrorFallback, RouteErrorBoundary };
export default RouteErrorBoundary;
